import classnames, { Argument } from 'classnames';
import { extendTailwindMerge } from 'tailwind-merge';

import { isTypographyScaleClass } from './typography-scale';

/**
 * `text-*` is ambiguous: `text-body-bold-12` sets a size, `text-gray-dark` sets a colour, and
 * tailwind-merge only knows the stock scale, so it reads every unfamiliar `text-…` as a colour.
 * Both then look like one class group and it drops one -- silently, with no error and nothing for
 * lint to see. `cn('text-body-bold-12', 'text-gray-dark')` returned only the colour, which is how a
 * 12px bold pill came out at the inherited 16px regular.
 *
 * Teaching it the size family fixes every call at once. The predicate is dependency-free and tested
 * on its own; see `typography-scale.ts`.
 */
const twMerge = extendTailwindMerge({
    extend: {
        classGroups: {
            'font-size': [{ text: [isTypographyScaleClass] }]
        }
    }
});

/**
 * Combines `classnames` and `tailwind-merge`:
 * - Accepts the same arguments as `classnames` (strings, arrays, objects, falsy values).
 * - Resolves conflicting Tailwind utility classes deterministically — the later class wins,
 *   without needing `!important` (`!`) overrides.
 *
 * @example
 * cn('justify-center', condition && 'justify-start') // → 'justify-start' wins when condition is true
 * cn('flex p-4', className) // → caller's className overrides defaults cleanly
 */
export const cn = (...args: Argument[]): string => twMerge(classnames(...args));
